import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Descriptions, Input, Modal, Space, Typography, message } from 'antd';
import { Consignment, ConsignmentClosePeriodResult } from '../../model/consignment';

export interface ClosePeriodPayload {
    expected_period_id: number;
    idempotency_key: string;
    note?: string;
}

interface Props {
    open: boolean;
    round: Consignment | null;
    onClose: () => void;
    onClosePeriod: (payload: ClosePeriodPayload) => Promise<ConsignmentClosePeriodResult>;
}

const genIdempotencyKey = (): string => {
    // crypto.randomUUID is available in all modern browsers; fall back for
    // older test/JSDOM environments that don't implement it.
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `close-period-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * "ปิดรอบบิล" (CLOSE_PERIOD) — distinct from "ปิดรายการฝากขายทั้งหมด"
 * (FINAL_CLOSE). Closing a Period settles outstanding sold-but-unpaid
 * quantities, closes the current Period, carries unsold stock forward, and
 * opens a new Period — the Consignment relationship itself stays ACTIVE.
 *
 * Sends `expected_period_id` (the Period the user actually saw as OPEN when
 * they opened this dialog) so the Backend can reject a stale/already-closed
 * request deterministically instead of silently closing whatever Period
 * happens to be open by the time the request is processed. One
 * `idempotency_key` is generated per intended close action (regenerated
 * only when this dialog is freshly opened, not on every render), so a
 * double-click or a network-level retry of the SAME intended action is
 * safe to replay rather than double-closing.
 */
export const ConsignmentClosePeriodModal: React.FC<Props> = ({ open, round, onClose, onClosePeriod }) => {
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState('');

    useEffect(() => {
        if (open) {
            setNote('');
            setIdempotencyKey(genIdempotencyKey());
        }
    }, [open, round?.id]);

    const currentPeriod = round?.current_period;

    const carryOverProducts = useMemo(() => {
        const activeBatchesWithRemaining = (round?.items || []).filter(
            it => it.period_id === currentPeriod?.id
                && (it.item_status || 'ACTIVE') === 'ACTIVE'
                && Number(it.latest_actual_remaining || 0) > 0
        );
        const productIds = new Set(activeBatchesWithRemaining.map(it => it.product_id));
        return productIds.size;
    }, [round, currentPeriod]);

    if (!round || !currentPeriod) return null;

    const handleConfirm = async () => {
        setSaving(true);
        try {
            const result = await onClosePeriod({
                expected_period_id: currentPeriod.id,
                idempotency_key: idempotencyKey,
                note: note.trim() || undefined
            });
            message.success(
                `ปิดรอบบิลที่ ${result.closed_period.period_no} สำเร็จ เปิดรอบบิลที่ ${result.new_period.period_no} แล้ว`
            );
            onClose();
        } catch (error: any) {
            const msg: string = error?.message || 'ปิดรอบบิลไม่สำเร็จ';
            if (msg.includes('STALE_PERIOD') || msg.includes('ALREADY_CLOSED')) {
                // Do NOT retry automatically against whatever Period is now
                // open -- that could be a different Period than the user
                // intended to close. Surface a clear message and let the
                // parent's refresh (after this modal closes) show the
                // caller the real current state.
                message.error('รอบบิลนี้มีการเปลี่ยนแปลงแล้ว กรุณารีเฟรชข้อมูลก่อนดำเนินการอีกครั้ง');
                onClose();
            } else {
                message.error(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            className="consignment-close-period-modal"
            open={open}
            title="🔒 ปิดรอบบิล"
            okText={`ยืนยันปิดรอบบิลที่ ${currentPeriod.period_no}`}
            cancelText="ยกเลิก"
            confirmLoading={saving}
            onOk={handleConfirm}
            onCancel={saving ? undefined : onClose}
            okButtonProps={{ danger: true, disabled: saving, 'data-testid': 'btn-confirm-close-period' } as any}
            cancelButtonProps={{ disabled: saving }}
            destroyOnClose
            width={560}
        >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Typography.Text>
                    ปิดรอบบิลที่ <strong>{currentPeriod.period_no}</strong> ของ <strong>{round.document_no}</strong> ({round.vendor_name || 'ผู้ฝากขาย'})?
                </Typography.Text>

                <Alert
                    type="warning"
                    showIcon
                    message="ระบบจะดำเนินการต่อไปนี้โดยอัตโนมัติ"
                    description={(
                        <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                            <li>ชำระยอดขายที่ยังค้างอยู่ทั้งหมดในรอบบิลนี้</li>
                            <li>ปิดรอบบิลที่ {currentPeriod.period_no} ปัจจุบัน (แก้ไขไม่ได้อีกหลังปิด)</li>
                            <li>ยกสินค้าคงเหลือ{carryOverProducts > 0 ? ` (${carryOverProducts} รายการสินค้า)` : ''} ไปยังรอบบิลใหม่ โดยคงต้นทุนเดิมของแต่ละล็อตไว้ ไม่เฉลี่ยราคา</li>
                            <li>สร้างรอบบิลที่ {currentPeriod.period_no + 1} เปิดใหม่ทันที</li>
                        </ul>
                    )}
                />

                <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="รอบบิลปัจจุบัน">
                        #{currentPeriod.period_no} — เปิดเมื่อ {currentPeriod.opened_at || '-'}
                    </Descriptions.Item>
                </Descriptions>

                <div>
                    <Typography.Text style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>หมายเหตุ</Typography.Text>
                    <Input
                        value={note}
                        onChange={event => setNote(event.target.value)}
                        placeholder="เช่น ปิดรอบบิลประจำสัปดาห์"
                        data-testid="input-close-period-note"
                        disabled={saving}
                    />
                </div>
            </Space>
        </Modal>
    );
};

export default ConsignmentClosePeriodModal;
